import { onUnmounted, ref } from 'vue-demi';
import type { Ref } from 'vue-demi';
import { isApiBusinessError, isApiServerError } from '@ark/web-api/frontend';
import {
  DRAW_STATUS,
  DELIVERY_STATUS,
  type AmoeSubmitReq,
  type AmoeSubmitRsp,
  type GetDrawResultRsp,
  type PrizeType,
} from '@ark/mets-shared';
import type { AmoeApiClient } from '../shared/api';
import { getAmoeBusinessCode, getAmoeErrorRouting } from '../shared/error-map';
import type { UseFormFieldsReturn } from './useFormFields';
import type { UseSmsCountdownReturn } from './useSmsCountdown';

// RH-B3 硬约束常量
const MAX_POLL_DURATION_MS = 60_000;
const POLL_INTERVALS_MS: readonly number[] = [2000, 4000, 8000];

// D-04 + UI-SPEC revision 2 两阶段 loading:9 业务 kind + deliveryLoading(含 sequence token RH-B3)
export type LotteryState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'deliveryLoading'; chancePublicId: string; token: number }
  | {
      kind: 'won';
      prize: { prizeId: number; prizeType: PrizeType; prizeName: string };
    }
  | { kind: 'lost' }
  | { kind: 'pending' }
  | { kind: 'alreadyMoomoo' }
  | { kind: 'duplicate' }
  | { kind: 'ipRejected' }
  | { kind: 'noEntries' }
  | { kind: 'noRound'; reason: 'not_started' | 'ended' | 'no_round' };

type UseLotteryOpts = {
  fields: UseFormFieldsReturn;
  sms: UseSmsCountdownReturn;
  getSeasonSign: () => string;
  onToast?: (msg: string) => void;
};

type AmoeSubmitFailureRsp = Extract<AmoeSubmitRsp, { success: false }>;
type GetDrawResultFailureRsp = Extract<GetDrawResultRsp, { success: false }>;

export type UseLotteryReturn = {
  state: Ref<LotteryState>;
  submit(): Promise<void>;
  sendSms(): Promise<void>;
  reset(): void;
};

/** sleep 可被 AbortSignal 中断;返回 true = 正常睡醒,false = 被 abort */
function waitOrAbort(delayMs: number, signal: AbortSignal): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (signal.aborted) {
      resolve(false);
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve(true);
    }, delayMs);
    function onAbort(): void {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      resolve(false);
    }
    signal.addEventListener('abort', onAbort);
  });
}

function isAmoeSubmitFailure(rsp: AmoeSubmitRsp): rsp is AmoeSubmitFailureRsp {
  return rsp.success === false;
}

function isDrawResultFailure(
  rsp: GetDrawResultRsp,
): rsp is GetDrawResultFailureRsp {
  return rsp.success === false;
}

export function useLottery(
  apiClient: AmoeApiClient,
  opts: UseLotteryOpts,
): UseLotteryReturn {
  const state = ref<LotteryState>({ kind: 'idle' });

  // RH-B3:AbortController + sequence token,reset / unmount 时触发 abort
  let controller = new AbortController();
  let tokenCounter = 0;

  onUnmounted(() => {
    controller.abort();
  });

  function toast(msg: string): void {
    if (opts.onToast) opts.onToast(msg);
  }

  function mapDrawResultToState(
    rsp: GetDrawResultRsp & { success: true },
  ): LotteryState {
    const ds = rsp.drawStatus;
    if (ds === DRAW_STATUS.WON) {
      if (!rsp.prize) {
        return { kind: 'pending' };
      }
      return {
        kind: 'won',
        prize: rsp.prize,
      };
    }
    if (ds === DRAW_STATUS.LOST) return { kind: 'lost' };
    // PENDING / FAILED 都收敛到 pending 终态
    return { kind: 'pending' };
  }

  /** RH-B5:submit/drawResult 的 success=false 分支分流 */
  function handleBusinessError(
    businessCode: number,
    availabilityReason?: 'not_started' | 'ended' | 'no_round',
  ): void {
    const routing = getAmoeErrorRouting(businessCode, availabilityReason);
    switch (routing.kind) {
      case 'alreadyMoomoo':
        state.value = { kind: 'alreadyMoomoo' };
        break;
      case 'duplicate':
        state.value = { kind: 'duplicate' };
        break;
      case 'ipRejected':
        state.value = { kind: 'ipRejected' };
        break;
      case 'noEntries':
        state.value = { kind: 'noEntries' };
        break;
      case 'noRound':
        state.value = { kind: 'noRound', reason: routing.reason };
        break;
      case 'toast':
        toast(routing.message);
        state.value = { kind: 'idle' };
        break;
    }
  }

  function handleApiError(err: unknown): void {
    const businessCode = getAmoeBusinessCode(err);
    if (businessCode !== undefined || isApiBusinessError(err)) {
      const code =
        businessCode ?? (err as { businessCode: number }).businessCode;
      handleBusinessError(code);
      return;
    }
    if (isApiServerError(err)) {
      toast('Network busy, please try again later.');
    } else {
      toast('Operation failed, please try again.');
    }
    state.value = { kind: 'idle' };
  }

  /** RH-B3:可靠终止的 Stage 2 轮询 */
  async function pollDrawResult(
    chancePublicId: string,
    token: number,
    signal: AbortSignal,
  ): Promise<void> {
    const startTs = Date.now();
    let attempt = 0;

    while (true) {
      // 状态 / token / signal 守门
      const s = state.value;
      if (signal.aborted) return;
      if (
        s.kind !== 'deliveryLoading' ||
        s.chancePublicId !== chancePublicId ||
        s.token !== token
      )
        return;

      // RH-B3 超时:切 pending(可关闭、可邮件通知)
      if (Date.now() - startTs >= MAX_POLL_DURATION_MS) {
        state.value = { kind: 'pending' };
        return;
      }

      const delay =
        POLL_INTERVALS_MS[Math.min(attempt, POLL_INTERVALS_MS.length - 1)];
      const slept = await waitOrAbort(delay, signal);
      if (!slept) return;

      // 再次守门(期间可能 reset / unmount / 新 submit)
      const s2 = state.value;
      if (signal.aborted) return;
      if (
        s2.kind !== 'deliveryLoading' ||
        s2.chancePublicId !== chancePublicId ||
        s2.token !== token
      )
        return;

      try {
        const rsp = await apiClient.getDrawResult(chancePublicId, signal);
        // 再次守门(request 期间可能 abort)
        const s3 = state.value;
        if (signal.aborted) return;
        if (
          s3.kind !== 'deliveryLoading' ||
          s3.chancePublicId !== chancePublicId ||
          s3.token !== token
        )
          return;

        if (isDrawResultFailure(rsp)) {
          handleBusinessError(rsp.businessCode);
          return;
        }
        const hasDrawStatus = rsp.drawStatus !== undefined;
        const delivered = rsp.deliveryStatus === DELIVERY_STATUS.DELIVERED;
        if (hasDrawStatus || delivered) {
          state.value = mapDrawResultToState(rsp);
          return;
        }
        // 未定,继续轮询
        attempt += 1;
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError' || signal.aborted)
          return;
        handleApiError(err);
        return;
      }
    }
  }

  async function submit(): Promise<void> {
    if (state.value.kind !== 'idle') return; // 幂等守门
    if (!opts.fields.validate()) return;

    const snap = opts.fields.snapshot();
    const req: AmoeSubmitReq = {
      firstName: snap.firstName,
      lastName: snap.lastName,
      age: Number(snap.age),
      countryCode: snap.countryCode,
      phoneNumber: snap.phoneNumber,
      addressLine1: snap.addressLine1,
      addressLine2:
        snap.addressLine2.length > 0 ? snap.addressLine2 : undefined,
      city: snap.city,
      state: snap.state,
      zipCode: snap.zipCode,
      email: snap.email,
      smsCode: snap.smsCode,
      seasonSign: opts.getSeasonSign(),
    };

    state.value = { kind: 'submitting' };
    try {
      const rsp = await apiClient.submit(req);
      if (isAmoeSubmitFailure(rsp)) {
        // RH-B5:reason 来自 rsp.reason 独立字段,不从业务文案取
        handleBusinessError(rsp.businessCode, rsp.reason);
        return;
      }
      // RH-B2:成功主路径**必须**进入 Stage 2,不再短路直返 drawStatus
      tokenCounter += 1;
      const token = tokenCounter;
      state.value = {
        kind: 'deliveryLoading',
        chancePublicId: rsp.chancePublicId,
        token,
      };
      void pollDrawResult(rsp.chancePublicId, token, controller.signal);
    } catch (err) {
      handleApiError(err);
    }
  }

  async function sendSms(): Promise<void> {
    if (state.value.kind !== 'idle') return;
    // RH-B4:canSend 已合并 smsSending 条件
    if (!opts.sms.canSend.value) return;

    const isPhoneValid = opts.fields.validate('phoneNumber');
    const isEmailValid = opts.fields.validate('email');
    if (!isPhoneValid || !isEmailValid) return;

    const snap = opts.fields.snapshot();
    if (snap.phoneNumber.length < 6 || snap.email.length === 0) return;

    opts.sms.markSending(true);
    try {
      const rsp = await apiClient.sendSms({
        countryCode: snap.countryCode,
        phoneNumber: snap.phoneNumber,
        email: snap.email,
        seasonSign: opts.getSeasonSign(),
      });
      if (rsp.isAlreadyMoomoo === true) {
        state.value = { kind: 'alreadyMoomoo' };
        return;
      }
      if (rsp.sent) {
        opts.sms.start();
      } else {
        toast("We couldn't send the code. Please try again.");
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      opts.sms.markSending(false);
    }
  }

  function reset(): void {
    // RH-B3:abort 旧 controller,让所有 in-flight poll 立即退出
    controller.abort();
    controller = new AbortController();
    tokenCounter += 1; // 旧 poll 即便侥幸未 abort 到,token 也已过期
    state.value = { kind: 'idle' };
  }

  return { state, submit, sendSms, reset };
}
